import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomInputText from "@/components/desktop/form/input/input-text/CustomInputText";
import CustomInputTextArea from "@/components/desktop/form/input/input-textarea/CustomInputTextArea";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";
import CustomInputCheckbox from "@/components/desktop/form/input/input-checkbox/CustomInputCheckbox";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";

import {
	useDistributionListsLookups,
	useDistributionList,
	useCreateDistributionList,
	useUpdateDistributionList,
} from "@/hooks/maintainers/distribution-lists/useDistributionLists";

import { base_member, base_object } from "../distribution-lists.constants";
import MembersSection from "./MembersSection";

const DistributionListForm = ({ isOpen, onClose, editingId, filters = {} }) => {
	const { data: lookups, isLoading: isLoadingLookups } = useDistributionListsLookups();
	const {
		data: detail,
		isLoading: isLoadingDetail,
		isFetching: isFetchingDetail,
	} = useDistributionList(editingId, {
		enabled: Boolean(editingId && isOpen),
	});

	const { mutate: createList, isLoading: isCreating } = useCreateDistributionList();
	const { mutate: updateList, isLoading: isUpdating } = useUpdateDistributionList();

	const [formState, setFormState] = useState(base_object);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!isOpen) {
			setFormState(base_object);
			setErrors({});
		}
	}, [isOpen]);

	useEffect(() => {
		if (detail && editingId) {
			const members = detail.members || [];
			const estateIds = (detail.estates_parents || []).map((e) => e.estate_parent_id).filter(Boolean);

			const notificationTypes = detail.notification_type
				? detail.notification_type
						.split(",")
						.map((type) => type.trim())
						.filter(Boolean)
				: [];

			setFormState({
				...base_object,
				...detail,
				members: members.length > 0 ? members : [{ ...base_member, temp_id: `${Date.now()}-${Math.random()}` }],
				estate_parent_ids: estateIds,
				notification_type: notificationTypes,
			});
		} else if (isOpen && !editingId) {
			setFormState((prev) => ({
				...prev,
				members: prev.members?.length > 0 ? prev.members : [{ ...base_member, temp_id: `${Date.now()}-${Math.random()}` }],
			}));
		}
	}, [detail, editingId, isOpen]);

	const estateParentOptions = useMemo(() => lookups?.estates_parents || [], [lookups]);

	const handleChange = (key, value) => {
		setFormState((prev) => ({ ...prev, [key]: value }));
		setErrors((prev) => ({ ...prev, [key]: undefined }));
	};

	const addMember = useCallback(() => {
		setFormState((prev) => ({
			...prev,
			members: [...(prev.members || []), { ...base_member, temp_id: `${Date.now()}-${Math.random()}` }],
		}));
	}, []);

	const updateMember = useCallback((index, patch) => {
		setFormState((prev) => {
			const next = [...(prev.members || [])];
			next[index] = { ...next[index], ...patch };
			return { ...prev, members: next };
		});
	}, []);

	const removeMember = useCallback((index) => {
		setFormState((prev) => ({ ...prev, members: prev.members.filter((_, i) => i !== index) }));
	}, []);

	const handleMemberUpdate = useCallback(
		(index, patch) => {
			updateMember(index, patch);
			setErrors((prev) => ({ ...prev, [`member_${index}`]: undefined }));
		},
		[updateMember],
	);

	const validate = () => {
		const nextErrors = {};

		if (!formState.name) nextErrors.name = "El nombre es requerido";
		if (!formState.estate_parent_ids || formState.estate_parent_ids.length === 0) nextErrors.estate_parent_ids = "Selecciona al menos un campo";
		if (!formState.notification_type || formState.notification_type.length === 0)
			nextErrors.notification_type = "Selecciona al menos un tipo de notificación";
		if (!formState.members || formState.members.length === 0) {
			nextErrors.members = "Agrega al menos un miembro";
		}

		(formState.members || []).forEach((member, idx) => {
			if (!member.name || !member.email) {
				nextErrors[`member_${idx}`] = "Nombre y correo son obligatorios";
			}
		});

		return nextErrors;
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		const nextErrors = validate();
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors);
			return;
		}

		const payload = {
			name: formState.name,
			description: formState.description,
			estate_parent_ids: formState.estate_parent_ids,
			is_active: formState.is_active,
			notification_type: Array.isArray(formState.notification_type) ? formState.notification_type.join(",") : formState.notification_type || "",
			members: (formState.members || []).map((m) => ({
				id: m.id || undefined,
				name: m.name,
				email: m.email,
				phone: m.phone,
			})),
		};

		const isEdit = Boolean(editingId);
		const action = isEdit ? updateList : createList;
		const params = isEdit ? { listId: detail?.id || formState.id, form: payload, filters } : { form: payload, filters };

		const extractErrorMessage = (err, fallback) => {
			const apiMessages = err?.response?.data?.messages;
			if (Array.isArray(apiMessages) && apiMessages.length > 0) return apiMessages.join(" \n");
			if (err?.response?.data?.message) return err.response.data.message;
			if (err?.message) return err.message;
			return fallback;
		};

		action(params, {
			onSuccess: () => {
				toast.success(isEdit ? "Lista actualizada" : "Lista creada");
				onClose?.();
				setFormState(base_object);
				setErrors({});
			},
			onError: (error) => {
				toast.error(extractErrorMessage(error, "No se pudo guardar la lista"));
			},
		});
	};

	const isLoading = isLoadingLookups || isLoadingDetail || isFetchingDetail || isCreating || isUpdating;

	return (
		<CustomModalForm
			open={isOpen}
			onClose={onClose}
			title={editingId ? "Editar lista de difusión" : "Nueva lista de difusión"}
			size="lg"
			asForm
			onSubmit={handleSubmit}
			isLoading={isLoading}
		>
			{isLoading ? (
				<CustomFetching center size="lg" />
			) : (
				<>
					<CustomInputText
						label="Nombre"
						name="name"
						value={formState.name}
						onChange={(value) => handleChange("name", value)}
						placeholder="Ingresa el nombre"
						required
						col={6}
						error={errors.name}
					/>

					<CustomInputSelect
						label="Campos"
						name="estate_parent_ids"
						options={estateParentOptions}
						value={formState.estate_parent_ids}
						onChange={(value) => handleChange("estate_parent_ids", value)}
						placeholder="Selecciona los campos"
						isMulti
						required
						col={6}
						error={errors.estate_parent_ids}
					/>

					<CustomInputTextArea
						label="Descripción"
						name="description"
						value={formState.description}
						onChange={(value) => handleChange("description", value)}
						placeholder="Ingresa una descripción"
						col={6}
						rows={2}
					/>

					<CustomInputCheckbox
						label="Notificar por"
						options={[
							{ value: "email", label: "Email" },
							{ value: "whatsapp", label: "WhatsApp" },
						]}
						value={formState.notification_type || []}
						onChange={(value) => handleChange("notification_type", value)}
						col={6}
						required
						error={errors.notification_type}
					/>

					<MembersSection members={formState.members} onAdd={addMember} onUpdate={handleMemberUpdate} onRemove={removeMember} errors={errors} />
				</>
			)}
		</CustomModalForm>
	);
};

export default DistributionListForm;
